import { useEffect, useRef, useState } from 'react';
import { usePatchIncidentPerson, type IncidentPerson, type IncidentSite, type Triage } from '../api';
import { ACCOUNTED, NEEDS, TRIAGE, formatTerm } from '../incidentVocab';

/**
 * Three-axis status entry for one person.
 *
 * `accounted` is the only axis the sweep always needs, so it gets the large
 * primary buttons and a single tap closes the popover. Triage and needs stay
 * collapsed until they are relevant — an operator working a street at 2am
 * should not have to skip past medical fields to say "she's fine".
 */
export default function StatusPopover({
  person, incidentId, sites, onClose,
}: {
  person: IncidentPerson;
  incidentId: string;
  sites: IncidentSite[];
  onClose: () => void;
}) {
  const patch = usePatchIncidentPerson();
  const ref = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(
    !!(person.triage || person.needs.length || person.disposition || person.found_at_site_id),
  );

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const save = (body: Parameters<typeof patch.mutate>[0]['body'], close = false) =>
    patch.mutate({ incidentId, id: person.id, body }, { onSuccess: close ? onClose : undefined });

  const toggleNeed = (need: string) =>
    save({ needs: person.needs.includes(need)
      ? person.needs.filter((n) => n !== need)
      : [...person.needs, need] });

  return (
    <div
      ref={ref}
      onClick={(e) => e.stopPropagation()}
      className="absolute z-30 mt-1 w-80 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 p-2 shadow-lg"
    >
      <div className="mb-1.5 flex items-baseline justify-between">
        <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{person.name}</span>
        {person.age != null && <span className="text-xs text-slate-400">{person.age}</span>}
      </div>

      {(person.medical_needs || person.special_needs.length > 0) && (
        <p className="mb-1.5 rounded bg-violet-50 dark:bg-violet-950/50 px-1.5 py-1 text-xs text-violet-800 dark:text-violet-300">
          {person.special_needs.map(formatTerm).join(', ')}
          {person.special_needs.length > 0 && person.medical_needs ? ' — ' : ''}
          {person.medical_needs}
        </p>
      )}

      {/* Axis 1 — the sweep. One tap and done. */}
      <div className="grid grid-cols-2 gap-1">
        {ACCOUNTED.map((a) => (
          <button
            key={a.value}
            onClick={() => save({ accounted: a.value }, true)}
            disabled={patch.isPending}
            className={`rounded px-2 py-2 text-sm font-medium ${
              person.accounted === a.value
                ? a.tone
                : 'border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
            }`}
          >
            {a.label}
          </button>
        ))}
      </div>

      <button
        onClick={() => setExpanded((v) => !v)}
        className="mt-1.5 w-full text-left text-xs text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
      >
        {expanded ? '▾' : '▸'} Injury, needs, and location
      </button>

      {expanded && (
        <div className="mt-1.5 space-y-2 border-t border-slate-100 dark:border-slate-800 pt-1.5">
          <Field label="Triage">
            <div className="flex flex-wrap gap-1">
              {TRIAGE.map((t) => (
                <button
                  key={t.value}
                  onClick={() => save({ triage: person.triage === t.value ? null : (t.value as Triage) })}
                  disabled={patch.isPending}
                  className={`rounded px-2 py-1 text-xs font-medium ${
                    person.triage === t.value
                      ? t.tone
                      : 'border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                  }`}
                >
                  {t.short} {t.label}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Needs">
            <div className="flex flex-wrap gap-1">
              {NEEDS.map((n) => (
                <button
                  key={n}
                  onClick={() => toggleNeed(n)}
                  disabled={patch.isPending}
                  className={`rounded px-1.5 py-0.5 text-xs ${
                    person.needs.includes(n)
                      ? 'bg-sky-600 text-white dark:bg-sky-500'
                      : 'border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                  }`}
                >
                  {formatTerm(n)}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Where they are">
            <input
              defaultValue={person.disposition ?? ''}
              onBlur={(e) => {
                const v = e.target.value.trim() || null;
                if (v !== (person.disposition ?? null)) save({ disposition: v });
              }}
              placeholder="at work, hospital, shelter…"
              className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-1.5 py-0.5 text-xs"
            />
          </Field>

          {/* An FK, not free text — so "the kids are at the Wilson house"
              can be counted and cross-referenced instead of parsed. */}
          <Field label="Found at another address">
            <select
              value={person.found_at_site_id ?? ''}
              onChange={(e) => save({ found_at_site_id: e.target.value || null })}
              className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-1.5 py-0.5 text-xs"
            >
              <option value="">— at their own address —</option>
              {sites.filter((s) => s.id !== person.site_id).map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
          </Field>

          <Field label="Medical notes">
            <input
              defaultValue={person.medical_needs ?? ''}
              onBlur={(e) => {
                const v = e.target.value.trim() || null;
                if (v !== (person.medical_needs ?? null)) save({ medical_needs: v });
              }}
              placeholder="injuries, medication, equipment…"
              className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-1.5 py-0.5 text-xs"
            />
          </Field>
        </div>
      )}

      {patch.error && (
        <p className="mt-1.5 rounded bg-rose-50 dark:bg-rose-950 px-1.5 py-1 text-xs text-rose-700 dark:text-rose-300">
          {(patch.error as Error).message}
        </p>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-0.5 text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">{label}</div>
      {children}
    </div>
  );
}
